import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';


/**
 * A class that implements the Storage interface and provides methods to interact with the local storage.
 */
class LocalStorage implements Storage {

  /**
   * The key-value pairs currently stored in the storage.
   */
  [name: string]: any;

  /**
   * The number of key-value pairs currently stored in the storage.
   */
  readonly length: number;

  /**
   * Removes all key-value pairs from the storage.
   */
  clear(): void {}

  /**
   * Returns the value associated with the given key, or null if the key does not exist.
   * @param key - The key to retrieve the value for.
   * @returns The value associated with the given key, or null if the key does not exist.
   */
  getItem(key: string): string | null {return null;}

  /**
   * Returns the name of the key at the given index, or null if the index is out of range.
   * @param index - The index of the key to retrieve the name for.
   * @returns The name of the key at the given index, or null if the index is out of range.
   */
  key(index: number): string | null {return null;}

  /**
   * Removes the key-value pair with the given key from the storage.
   * @param key - The key of the key-value pair to remove.
   */
  removeItem(key: string): void {}

  /**
   * Sets the value for the given key.
   * @param key - The key to set the value for.
   * @param value - The value to set for the given key.
   */
  setItem(key: string, value: string): void {}
}


/**
 * A service for interacting with the browser's local storage.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService implements Storage {

  /**
   * The underlying storage object used to store key-value pairs.
   */
  private storage: Storage;

  /**
   * A flag indicating whether the code is running in a browser environment.
   */
  private _isBrowser: boolean = false;

  /**
   * Creates a new instance of the LocalStorageService class.
   * @param _platformId The platform ID token provided by Angular's DI system.
   */
  constructor(
    @Inject(PLATFORM_ID) private _platformId: any,
  ) {
    this.storage = new LocalStorage();
    if (isPlatformBrowser(this._platformId)) {
      this.storage = localStorage;
      this._isBrowser = true;
    }
  }

  /**
   * The key-value pairs currently stored in the storage object.
   */
  [name: string]: any;

  /**
   * The number of key-value pairs currently stored in the storage object.
   */
  length: number;

  /**
   * Removes all key-value pairs from the storage object.
   */
  clear(): void {
    this.storage.clear();
  }

  /**
   * Retrieves the value associated with the specified key from the storage object.
   * @param key The key to retrieve the value for.
   * @returns The value associated with the specified key, or null if the key does not exist.
   */
  getItem(key: string): string | null {
    return this.storage.getItem(key);
  }

  /**
   * Retrieves the key at the specified index from the storage object.
   * @param index The index of the key to retrieve.
   * @returns The key at the specified index, or null if the index is out of range.
   */
  key(index: number): string | null {
    return this.storage.key(index);
  }

  /**
   * Removes the key-value pair associated with the specified key from the storage object.
   * @param key The key to remove the value for.
   */
  removeItem(key: string): void {
    return this.storage.removeItem(key);
  }

  /**
   * Sets the value associated with the specified key in the storage object.
   * @param key The key to set the value for.
   * @param value The value to associate with the specified key.
   */
  setItem(key: string, value: string): void {
    return this.storage.setItem(key, value);
  }
}